import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectImages, selectCurrentSlideIndex, setCurrentSlide } from '../ConfigurationContent/ConfigurePresentationSlice';
import { useContentIsActive } from '../../components/FeatureCard/ContentIsActiveContext';
import { Button, Icon } from '@blueprintjs/core';
import { emitJson, emitMsg } from '../websocket/websocketSlice'
import './PresentationControlPanel.css';

/**
 * This component can be used to control a previously uploaded presentation. 
 * It is intended to be used within a FeatureCard environment.
 * @returns { JSX.Element } The rendered `PresentationControlPanel` component.
 */
const PresentationControlPanel = () => {
    const dispatch = useDispatch();
    const images = useSelector(selectImages);
    const currentSlideIndex = useSelector(selectCurrentSlideIndex);
    const { contentIsActive, setContentIsActive } = useContentIsActive(); // tell FeatureCard if content is active or not
    const [isPresentationVisible, setPresentationVisible] = useState(true);
    const [hasSentSlides, setHasSentSlides] = useState(false);

    // detect if slides are uploaded or not, inform FeatureCard
    useEffect(() => {
        setContentIsActive(images.length > 0);
    }, [images, setContentIsActive]);

    // initial send slides if available
    useEffect(() => {
        if (isPresentationVisible && !hasSentSlides) {
            sendSlidesToUnity();
        }
    }, [isPresentationVisible, hasSentSlides]);

    // Function for switching the presentation (Show/Hide)
    const togglePresentationVisibility = () => {
        setPresentationVisible(!isPresentationVisible);

        if (isPresentationVisible) {
            emitJson("clearPresentation", {});
            setHasSentSlides(false);
        }
    };

    // Get the base64images and send them to Unity
    const sendSlidesToUnity = () => {
        if (hasSentSlides) return;

        Promise.all(
            images.map((imageObj) => {
                const url = imageObj.url;

                return fetch(url)
                    .then((res) => res.blob()) // Convert to blob
                    .then((blob) => {
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract the Base64 string
                            reader.readAsDataURL(blob); // Convert Blob to Base64
                        });
                    });
            })
        ).then((base64Images) => {
            const presentationData = {
                slides: base64Images,  // Base64-Encoded Images
                command: "loadPresentation",
                aspectRatio: 4 / 3,
            };

            emitJson("loadPresentation", presentationData);
            console.log("sendSlidesToUnity, data: ", presentationData);

            setHasSentSlides(true);
        }).catch((error) => {
            console.error("Error when sending images to Unity: ", error);
        });
    };

    // Navigation to the next slide
    const goToNextSlide = () => {
        if (currentSlideIndex < images.length - 1) {
            const nextIndex = currentSlideIndex + 1;
            dispatch(setCurrentSlide(nextIndex));
            emitMsg("changeSlide", nextIndex);
        }
    };

    // Navigation to the previous slide
    const goToPreviousSlide = () => {
        if (currentSlideIndex > 0) {
            const prevIndex = currentSlideIndex - 1;
            dispatch(setCurrentSlide(prevIndex));
            emitMsg("changeSlide", prevIndex);
        }
    };

    /* No presentation was uploaded: */
    if (!contentIsActive) {
        return (
            <div className="presentation-control-panel">
                <h6>No presentation uploaded</h6>
            </div>
        );
    }

    /* Presentation was uploaded: */
    return (
        <div className="presentation-control-panel">
            {/* SHOW/HIDE */}
            <p className="button-with-left-icon">
                <Button onClick={togglePresentationVisibility}>
                    <Icon icon={isPresentationVisible ? "eye-off" : "eye-open"} style={{ marginRight: "5px" }} />
                    {(isPresentationVisible ? "Turn off smart board" : "Turn on smart board")}
                </Button>
            </p>

            {/* PREVIEW */}
            <div className="slides-preview">
                <div className="current-slide-preview">
                    <h6>Current</h6>
                    <img src={images[currentSlideIndex].url} alt={`Slide ${currentSlideIndex + 1}`} />
                </div>
                <div className="next-slide-preview">
                    <h6>Next</h6>
                    {currentSlideIndex < images.length - 1 && (
                        <img src={images[currentSlideIndex + 1].url} alt={`Slide ${currentSlideIndex + 2}`} />
                    )}
                </div>
            </div>

            {/* BUTTONS */}
            <div className="button-group">
                {/* PREV */}
                <p className="button-with-icon">
                    <Button
                        onClick={goToPreviousSlide}
                        disabled={currentSlideIndex === 0}
                    >
                        <Icon icon="double-chevron-left" style={{ marginRight: "3px" }} />
                    </Button>
                </p>
                {/* INFO */}
                <span className="slide-counter">Slide {currentSlideIndex + 1} / {images.length}</span>
                {/* NEXT */}
                <p className="button-with-icon">
                    <Button
                        onClick={goToNextSlide}
                        disabled={currentSlideIndex === images.length - 1}
                    >
                        <Icon icon="double-chevron-right" style={{ marginLeft: "3px" }} />
                    </Button>
                </p>
            </div>
        </div>
    );
};

export default PresentationControlPanel;